import { ApiError, StaleError } from '@/api/client';
import {
  type ApiUserFull,
  type UserWrite,
  useAbsences,
  useRoles,
  useShifts,
  useTeams,
  useUser,
  useUserWrites,
  useUsers,
} from '@/api/data';
import { useCan } from '@/auth/caps';
/**
 * The employee page (owner brief, 2026-09-21): header, four figures and seven
 * tabs (Tilgjengelighet since issue #70), the address naming the tab (/ansatte/14/fravaer). Reads GET
 * /admin/users/:id; edits go through the Rediger ansatt drawer, one PUT on Lagre
 * (ansatt/EditDrawer.tsx). Company access keeps its own save (Bedriftstilgang).
 */
import { T } from '@/i18n';
import { breakPolicy, oslo, today } from '@/rules';
import { modeOf } from '@/rules/breakSetting';
import { setDirty, useDirty } from '@/shell/context';
import { useTenant } from '@/shell/tenant';
import { Button, Drawer, Empty, Menu, Panel, Skeleton, toast } from '@/ui';
import { fullName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { Notice, PageHead, Status, TabPanel, Tabs } from '@/ui/page';
import { addDays, mondayOf } from '@timefly/core';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import './ansatt/ansatte.css';
import { Files } from './Files';
import { EditDrawer, tabOfField } from './ansatt/EditDrawer';
import { FravaerTab } from './ansatt/FravaerTab';
import { Figures, Oversikt } from './ansatt/Oversikt';
import { TidTab } from './ansatt/TidTab';
import { TilgangTab } from './ansatt/TilgangTab';
import { TilgjengelighetTab } from './ansatt/TilgjengelighetTab';
import { VakterTab } from './ansatt/VakterTab';
import { fullBody } from './ansatt/body';
import { EmailTypo } from './ansatt/emailTypo';
import { useAccountMail } from './ansatt/mail';
import { roleIn, takenRefusal } from './ansatt/roles';
import { neighbours, setupItems } from './ansatt/roster';
import { nextShift, onDuty, weekFigures } from './ansatt/summary';

const TABS = [
  { key: '', label: 'Oversikt' },
  { key: 'vakter', label: 'Vakter' },
  { key: 'tid', label: 'Tid & lønn' },
  { key: 'fravaer', label: 'Fravær' },
  { key: 'tilgjengelighet', label: 'Tilgjengelighet' },
  { key: 'dokumenter', label: 'Dokumenter' },
  { key: 'tilgang', label: 'Tilgang' },
] as const;
type Tab = (typeof TABS)[number]['key'];

function Row({ label, children, note }: { label: string; children: React.ReactNode; note?: string }) {
  return (
    <div className="prow">
      <b>{label}</b>
      <span className="pv">
        {children}
        {note && <em>{note}</em>}
      </span>
    </div>
  );
}

export default function Ansatt() {
  const { id, fane } = useParams();
  const isNew = id === 'ny';
  const uid = isNew ? null : Number(id);
  const can = useCan();
  const nav = useNavigate();
  // added from the first-run setup: back there once the person is made (owner, 2026-09-27)
  const [sp, setSp] = useSearchParams();
  const fromSetup = sp.get('fra') === 'oppsett';
  // "su rodyklėm eiti pirmyn/atgal per kitus darbuotojus": filling in details for a lot of
  // people meant walking back to the list every time (product user, 2026-09-21). The list is
  // already in the cache, so this costs no request.
  const rosterQ = useUsers();
  const { tenant } = useTenant();
  // the company's week a new full-timer follows; 37,5 where the company has set none (plan 0027 B)
  const companyWeek = String(
    tenant?.agreed_week_hours != null ? Number(tenant.agreed_week_hours) : 37.5,
  ).replace('.', ',');
  const userQ = useUser(uid);
  const teamsQ = useTeams();
  const w = useUserWrites();
  const mail = useAccountMail();
  const monday = mondayOf(today());
  // four weeks back for the registrations, five ahead for the next shift
  const shiftsQ = useShifts(addDays(monday, -28), addDays(monday, 34));
  const absQ = useAbsences();
  // the company's own roles, so the card names the role the company gave it; above the early
  // returns because a hook runs on every render or on none
  const rolesQ = useRoles();
  const [nw, setNw] = useState<UserWrite>({
    contract_type: 'hourly',
    active_status: true,
    send_welcome: true,
  });
  // plan 0027 B: a new person is full time (follows the company's week) or part time with their
  // own week; asked as a choice, so an empty field never passes for full time again
  const [week, setWeek] = useState<{ kind: 'heltid' | 'deltid' | null; hours: string }>({
    kind: null,
    hours: '',
  });
  const [editing, setEditing] = useState<
    false | { tab: 'person' | 'arbeid' | 'team'; focus?: string | undefined }
  >(false);
  // ?rediger=<field>: the roster's setup tag opens the editor on that field (manager redesign,
  // 2026-10-09); the address is cleared once it has been read, so Back does not reopen it
  const askedField = sp.get('rediger');
  useEffect(() => {
    if (!askedField || !userQ.data || !can('ansatte.endre')) return;
    setEditing({ tab: tabOfField(askedField), focus: askedField });
    const n = new URLSearchParams(sp);
    n.delete('rediger');
    setSp(n, { replace: true });
  }, [askedField, userQ.data, can, sp, setSp]);
  const edit = (focus?: string) => setEditing({ tab: focus ? tabOfField(focus) : 'person', focus });
  useDirty('new-employee', isNew && Object.keys(nw).length > 2, T('Ny ansatt'));
  /**
   * Sett nytt passord (TF-WEB-COMPLETION B). The password travels only from this
   * drawer, never from the blur edits: fullBody reads the record from the GET,
   * which carries no password, and no field() ever names it. `unknown` is set
   * when no answer arrived; the drawer then says so and offers no resend.
   */
  const [pw, setPw] = useState<{
    a: string;
    b: string;
    busy: boolean;
    msg: string | null;
    unknown: boolean;
  } | null>(null);

  const u = userQ.data;
  /**
   * PUT /admin/users/:id is a full replace: every field in the body overwrites the
   * column, an absent one writes null, and username, name, lastname, phone,
   * address, email and gender go through toLowerCase(), so they must be strings
   * (admin/userController.js updateUser). The whole record goes with every change.
   */
  const patch = async (body: UserWrite, label = T('Lagret')) => {
    if (!u) return;
    try {
      await w.update.mutateAsync({ id: u.id, body: fullBody(u, body) });
      toast(label);
    } catch (e) {
      toast(takenRefusal(e) ?? T('Kunne ikke lagre: {error}', { error: (e as Error).message }));
    }
  };

  if (isNew) {
    const create = async () => {
      // the birth date too: the rules for workers under 18 need the age, and nobody is planned without it (plan 0027 batch A)
      // the address may be left empty (2026-09-29): no welcome mail and no password then, so the
      // person cannot log in until one is added through Rediger
      const noEmail = !nw.email?.trim();
      const miss = [
        'username',
        'name',
        'lastname',
        'birth_date',
        ...(nw.send_welcome || noEmail ? [] : ['password']),
      ].filter((k) => !(nw as Record<string, unknown>)[k]);
      if (miss.length) {
        toast(T('Mangler: {list}', { list: miss.join(', ') }));
        return;
      }
      // required: heltid follows the company's week, deltid needs the person's own (plan 0027 B)
      if (!week.kind) {
        toast(T('Mangler: avtalt arbeidstid, heltid eller deltid'));
        return;
      }
      const own = Number(week.hours.trim().replace(',', '.'));
      if (
        week.kind === 'deltid' &&
        !(Number.isFinite(own) && own > 0 && own <= 40 && Math.round(own * 100) === own * 100)
      ) {
        toast(T('Deltid: timer per uke, et tall over 0 og høyst 40, f.eks. 22,5'));
        return;
      }
      // aml § 14-6 (1) e (#108): a temporary job states its end; the server refuses it too
      if (nw.employment_form === 'midlertidig' && !nw.end_date) {
        toast(T('Mangler: sluttdato for midlertidig ansettelse'));
        return;
      }
      try {
        // with the welcome box ticked no password is sent: the person sets one from the mail
        const { password: _typed, max_hours_per_week: _old, hourly_rate: hr, monthly_rate: mr, ...rest } = nw;
        // pay set when the person is made (owner, 2026-09-27): only the kind chosen, commas read as decimals
        const rate = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim().replace(',', '.') : null);
        const m = rate(mr);
        const h = rate(hr);
        const pay =
          nw.contract_type === 'monthly' ? (m ? { monthly_rate: m } : {}) : h ? { hourly_rate: h } : {};
        const body = {
          ...rest,
          ...pay,
          agreed_week_hours: week.kind === 'deltid' ? own : null,
          // the contract (#108): the form, and the end date only with a form that has one
          employment_form: nw.employment_form || null,
          end_date: nw.employment_form === 'midlertidig' ? (nw.end_date ?? null) : null,
        };
        const made = await w.create.mutateAsync(
          noEmail
            ? { ...body, email: '', send_welcome: false }
            : nw.send_welcome
              ? body
              : { ...body, ...(nw.password ? { password: nw.password } : {}) },
        );
        toast(
          noEmail
            ? T('Ansatt opprettet uten e-post. Legg inn adressen under Rediger, så kan personen logge inn.')
            : made.welcome === 'sent'
              ? T('Ansatt opprettet. Velkomst-e-post sendt til {e}.', { e: nw.email ?? '' })
              : made.welcome
                ? T(
                    'Ansatt opprettet, men velkomst-e-posten ble ikke sendt. Send den fra menyen på den ansatte.',
                  )
                : T('Ansatt opprettet. Sett avtalt tid og sats.'),
        );
        // the form is saved: only its own registration is dropped, so the move to the new card (or
        // back to /oppsett) is not stopped by «Ny ansatt er ikke lagret» (TF-REG03, P8). A refused or
        // failed create never gets here and keeps both the draft and the guard
        setDirty('new-employee', null);
        nav(fromSetup ? '/oppsett' : `/ansatte/${made.id}`);
      } catch (e) {
        toast(takenRefusal(e) ?? T('Kunne ikke opprette: {error}', { error: (e as Error).message }));
      }
    };
    const inp = (k: keyof UserWrite, label: string, type = 'text') => (
      <Row label={label}>
        <input
          className="inp"
          type={type}
          aria-label={label}
          style={{ width: 240 }}
          value={(nw[k] as string) ?? ''}
          onChange={(e) => setNw({ ...nw, [k]: e.target.value })}
        />
      </Row>
    );
    return (
      <div className="ans-prof">
        <div className="ans-top">
          <Link to="/ansatte" className="ans-back">
            ← {T('Ansatte')}
          </Link>
        </div>
        <PageHead title={T('Ny ansatt')} sub={T('Start med personen og avtalt arbeidstid.')} />
        <Panel title={T('Person')} sub={T('det API-et krever for å opprette')}>
          {/* TF-REG03 (REG03-02): the whole form is locked while the create is on its way, so nothing typed
              then can be lost when the answer moves the page on; a refusal unlocks it with the draft kept */}
          <fieldset className="ans-newfs" disabled={w.create.isPending} aria-busy={w.create.isPending}>
            {inp('name', T('Fornavn'))}
            {inp('lastname', T('Etternavn'))}
            {inp('birth_date', T('Fødselsdato'), 'date')}
            {inp('username', T('Brukernavn'))}
            <Row label={T('E-post')}>
              <input
                className="inp"
                type="email"
                aria-label={T('E-post')}
                style={{ width: 240 }}
                placeholder={T('Kan stå tom')}
                value={nw.email ?? ''}
                onChange={(e) => setNw({ ...nw, email: e.target.value })}
              />
              <EmailTypo value={nw.email ?? ''} onFix={(email) => setNw({ ...nw, email })} />
            </Row>
            {nw.email?.trim() ? (
              <>
                <Row label={T('Innlogging')}>
                  <label className="chk">
                    <input
                      type="checkbox"
                      checked={!!nw.send_welcome}
                      onChange={(e) => setNw({ ...nw, send_welcome: e.target.checked })}
                    />
                    {T('Send velkomst-e-post med lenke til eget passord')}
                  </label>
                </Row>
                {!nw.send_welcome && inp('password', T('Passord'), 'password')}
              </>
            ) : (
              <Row label={T('Innlogging')}>
                <span className="hint" data-testid="no-email-note">
                  {T(
                    'Ingen e-post ennå. Personen kan planlegges. For å logge inn trenger personen en adresse, eller lenken fra «Vis lenke» på Ansatte.',
                  )}
                </span>
              </Row>
            )}
            {inp('phone', T('Telefon'))}
            <Row label={T('Lønnstype')}>
              <div className="seg">
                <button
                  type="button"
                  aria-pressed={nw.contract_type !== 'monthly'}
                  onClick={() => setNw({ ...nw, contract_type: 'hourly' })}
                >
                  {T('Timelønn')}
                </button>
                <button
                  type="button"
                  aria-pressed={nw.contract_type === 'monthly'}
                  onClick={() => setNw({ ...nw, contract_type: 'monthly' })}
                >
                  {T('Fastlønn')}
                </button>
              </div>
            </Row>
            {can('ansatte.loenn') &&
              (nw.contract_type === 'monthly'
                ? inp('monthly_rate', T('Månedslønn, kr'))
                : inp('hourly_rate', T('Timelønn, kr per time')))}
            <Row
              label={T('Avtalt arbeidstid')}
              note={T(
                'Heltid følger bedriftens uke (Innstillinger → Arbeidstid). Deltid: timer i en vanlig uke. Må velges.',
              )}
            >
              <div className="seg">
                <button
                  type="button"
                  aria-pressed={week.kind === 'heltid'}
                  onClick={() => setWeek({ ...week, kind: 'heltid' })}
                >
                  {T('Heltid (følger bedriften, {h} t)', { h: companyWeek })}
                </button>
                <button
                  type="button"
                  aria-pressed={week.kind === 'deltid'}
                  onClick={() => setWeek({ ...week, kind: 'deltid' })}
                >
                  {T('Deltid')}
                </button>
              </div>
              {week.kind === 'deltid' && (
                <input
                  className="inp"
                  inputMode="decimal"
                  aria-label={T('Deltid, timer per uke')}
                  style={{ width: 90, marginTop: 6 }}
                  placeholder="22,5"
                  value={week.hours}
                  onChange={(e) => setWeek({ ...week, hours: e.target.value })}
                />
              )}
            </Row>
            <Row
              label={T('Ansettelsesform')}
              note={T(
                'Fast, midlertidig eller tilkalling, som arbeidsavtalen sier (arbeidsmiljøloven § 14-9).',
              )}
            >
              <select
                className="inp"
                aria-label={T('Ansettelsesform')}
                style={{ width: 240 }}
                value={nw.employment_form ?? ''}
                onChange={(e) =>
                  setNw({
                    ...nw,
                    employment_form: (e.target.value || null) as 'fast' | 'midlertidig' | 'tilkalling' | null,
                  })
                }
              >
                <option value="">{T('Ikke oppgitt')}</option>
                <option value="fast">{T('Fast')}</option>
                <option value="midlertidig">{T('Midlertidig')}</option>
                <option value="tilkalling">{T('Tilkalling')}</option>
              </select>
            </Row>
            {nw.employment_form === 'midlertidig' && inp('end_date', T('Sluttdato'), 'date')}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <span className="sp" />
              <Button onClick={() => nav('/ansatte')}>{T('Avbryt')}</Button>
              <Button tone="pri" onClick={create} disabled={w.create.isPending}>
                <Icon name="plus" />
                {T('Opprett')}
              </Button>
            </div>
          </fieldset>
        </Panel>
      </div>
    );
  }

  if (userQ.isError)
    return <Empty>{T('Fant ikke personen: {error}', { error: (userQ.error as Error).message })}</Empty>;
  if (!u) return <Skeleton />;
  const name = fullName(u);
  // the job title: the list carries the company's role name as a fallback, the single read does not
  const jobTitle = (rosterQ.data ?? []).find((x) => x.id === u.id)?.title ?? u.title ?? null;
  const step = neighbours(rosterQ.data ?? [], u.id);
  const mine = (shiftsQ.data?.shifts ?? []).filter((s) => s.user_id === u.id);
  const policy = breakPolicy(tenant);
  const abs = (absQ.data ?? [])
    .filter((a) => a.user_id === u.id)
    .sort((a, b) => b.start_date.localeCompare(a.start_date));
  const teams = (teamsQ.data ?? []).filter((t) => t.name);
  const seeLoenn = can('ansatte.loenn');
  const seeSens = can('ansatte.pnr');
  const mayEdit = can('ansatte.endre');
  // GET /admin/users/:id includes every membership row (admin/userController.js getUserById)
  const memberships = (u as ApiUserFull & { userTenantsList?: { tenant_id: number; role?: string }[] })
    .userTenantsList;
  const companies = (memberships ?? []).length;
  // the role in the active company (user_tenants), the same source Bedriftstilgang edits; users.role is never shown
  const roleHere = tenant ? roleIn(memberships, tenant.id) : null;
  // named from the company's own roles, so a company that calls its role Vaktleder reads as
  // Vaktleder here rather than as the nearest fixed word (custom roles, 2026-09-16)
  const roleHereText =
    roleHere == null
      ? T('Ingen tilgang til denne bedriften')
      : (rolesQ.data?.roles.find((r) => r.key === roleHere)?.name ?? roleHere);
  const setPassword = async () => {
    if (!pw) return;
    // the backend's only rule: a non-empty string (admin/userController.js updateUser `if (password)`)
    if (!pw.a) return setPw({ ...pw, msg: T('Skriv inn et passord') });
    if (pw.a !== pw.b) return setPw({ ...pw, msg: T('Passordene er ikke like') });
    setPw({ ...pw, busy: true, msg: null });
    try {
      await w.update.mutateAsync({ id: u.id, body: fullBody(u, { password: pw.a }) });
      toast(T('Nytt passord er satt'));
      setPw(null);
    } catch (e) {
      // an ApiError is the server's own refusal; anything else (network, a discarded answer) is no answer
      const refused = e instanceof ApiError && !(e instanceof StaleError);
      setPw({
        a: refused ? pw.a : '',
        b: refused ? pw.b : '',
        busy: false,
        unknown: !refused,
        msg: refused
          ? `${T('Kunne ikke sette passord')}: ${(e as Error).message}`
          : T('Resultatet er ukjent. Passordet kan være endret. Ingenting ble sendt på nytt.'),
      });
    }
  };

  // A manager reaches this page for the directory their scheduling and approvals need, and
  // the server sends them a name, a work contact, the teams in this company and nothing else.
  // Rendering the edit form with pieces hidden would show a status button and autosaving
  // fields the server refuses, and would read withheld values as missing ones: the role would
  // claim no access because the membership list is not in the answer, and every balance would
  // look unset. So this is its own view, with only what arrived (TF-SIMPLE-ROLES acceptance).
  if (!mayEdit) {
    const teamsHere = (u.teams ?? []).map((t) => t.name).filter(Boolean);
    return (
      <div className="ans-prof">
        <div className="ans-top">
          <Link to="/ansatte" className="ans-back">
            ← {T('Ansatte')}
          </Link>
        </div>
        <PageHead
          title={name}
          sub={[jobTitle || T('Stilling ikke oppgitt'), teamsHere.join(', ') || T('Ingen team')].join(' · ')}
          actions={
            u.active_status ? (
              <Status tone="ok">{T('Aktiv')}</Status>
            ) : (
              <Status tone="neu">{T('Inaktiv')}</Status>
            )
          }
        />
        <Panel title={T('Kontakt og team')} sub={T('Det du trenger for vaktplan og godkjenning')}>
          <Row label={T('E-post')}>
            <span>{u.email || T('Ingen e-post ennå')}</span>
          </Row>
          <Row label={T('Telefon')}>
            <span>{u.phone || '–'}</span>
          </Row>
          <Row label={T('Team')}>
            <span>{teamsHere.length ? teamsHere.join(', ') : T('Ingen team')}</span>
          </Row>
        </Panel>
        <p className="hint" style={{ maxWidth: '66ch' }} data-testid="directory-note">
          {T(
            'Personopplysninger, lønn, saldo og roller endres av en administrator. Du ser det du trenger for vaktplan, timeliste og fravær.',
          )}
        </p>
        {/* issue #70: whoever approves availability decides here without managing the person */}
        {can('tilgjengelighet.godkjenne') && tenant?.availability_requires_approval && (
          <Panel title={T('Tilgjengelighet')}>
            <TilgjengelighetTab userId={u.id} />
          </Panel>
        )}
      </div>
    );
  }

  const base = `/ansatte/${u.id}`;
  // account mail (owner, 2026-09-21): outward-facing, so the address is confirmed first
  const sendMail = (kind: 'welcome' | 'reset-link') => mail(u, kind);
  const tab = TABS.some((x) => x.key === fane) ? (fane as Tab) : '';
  const now = Date.now();
  const next = nextShift(mine, now);
  const duty = onDuty(mine, now);
  const wk = weekFigures(mine, monday, addDays(monday, 6), policy, u.max_hours_per_week);
  const teamNames = (u.teams ?? []).map((t) => t.name).filter(Boolean);
  const stepTo = (other: { id: number }) => nav(`/ansatte/${other.id}${tab ? `/${tab}` : ''}`);
  const gaps = setupItems(u, mayEdit);
  const statusText = !u.active_status ? (
    <span className="aduty off">{T('Inaktiv')}</span>
  ) : duty ? (
    <span className="aduty on">
      {T('På jobb nå, siden {t}', { t: oslo(duty.punch_in_time)?.time?.slice(0, 5) ?? '' })}
    </span>
  ) : (
    <span className="aduty">{T('Ikke på jobb nå')}</span>
  );
  return (
    <div className="ans-prof">
      <div className="ans-top">
        <Link to="/ansatte" className="ans-back">
          ← {T('Ansatte')}
        </Link>
        <span className="sp" />
        {step.total > 1 && step.index > 0 && (
          <span className="stepper" data-testid="ansatt-stepper">
            <button
              type="button"
              className="stepbtn"
              disabled={!step.prev}
              onClick={() => step.prev && stepTo(step.prev)}
              aria-label={step.prev ? `${T('Forrige')}: ${fullName(step.prev)}` : T('Forrige')}
              title={step.prev ? fullName(step.prev) : undefined}
            >
              <Icon name="chevronLeft" />
            </button>
            <span className="stepn">
              {step.index} {T('av')} {step.total}
            </span>
            <button
              type="button"
              className="stepbtn"
              disabled={!step.next}
              onClick={() => step.next && stepTo(step.next)}
              aria-label={step.next ? `${T('Neste')}: ${fullName(step.next)}` : T('Neste')}
              title={step.next ? fullName(step.next) : undefined}
            >
              <Icon name="chevronRight" />
            </button>
          </span>
        )}
      </div>
      <PageHead
        title={name}
        sub={
          <span className="sub">
            {jobTitle || T('Stilling ikke oppgitt')}
            {' · '}
            {teamNames.length > 0 ? teamNames.join(', ') : T('Ingen team')}
            {' · '}
            {statusText}
          </span>
        }
        actions={
          <>
            {u.active_status ? (
              <Status tone="ok">{T('Aktiv')}</Status>
            ) : (
              <Status tone="neu">{T('Inaktiv')}</Status>
            )}
            <Button onClick={() => edit()}>{T('Rediger')}</Button>
            <Menu
              tip={T('Konto og status')}
              trigger={
                <button type="button" className="btn" aria-label={T('Flere valg')}>
                  <Icon name="more" />
                </button>
              }
              items={[
                {
                  key: 'active',
                  label: u.active_status ? T('Sett inaktiv') : T('Sett aktiv'),
                  onSelect: () =>
                    patch(
                      { active_status: !u.active_status },
                      u.active_status ? T('Satt inaktiv') : T('Satt aktiv'),
                    ),
                },
                ...(u.active_status && u.email
                  ? [
                      {
                        key: 'welcome',
                        label: T('Send velkomst-e-post'),
                        onSelect: () => sendMail('welcome'),
                      },
                      {
                        key: 'reset',
                        label: T('Send lenke for nytt passord'),
                        onSelect: () => sendMail('reset-link'),
                      },
                    ]
                  : []),
                // offered only for an account whose sole membership is this company (Codex resolution 2026-09-14); the server enforces the same rule
                ...(companies === 1
                  ? [
                      {
                        key: 'pw',
                        label: T('Sett nytt passord…'),
                        onSelect: () => setPw({ a: '', b: '', busy: false, msg: null, unknown: false }),
                      },
                    ]
                  : []),
              ]}
            />
          </>
        }
      >
        <Tabs
          label={T('Profil')}
          value={tab}
          items={TABS.map((x) => ({ key: x.key, label: T(x.label), to: x.key ? `${base}/${x.key}` : base }))}
        />
      </PageHead>
      {gaps.length > 0 && (
        <Notice tone="warn" title={T('Oppsett mangler')}>
          <span className="ans-gaps">
            {gaps.map((g) => (
              <button key={g.field} type="button" className="lnk" onClick={() => edit(g.field)}>
                {T(g.text)}
              </button>
            ))}
          </span>
        </Notice>
      )}
      <Drawer
        open={!!pw}
        onClose={() => setPw(null)}
        blockClose={() => !!pw?.busy}
        title={T('Sett nytt passord')}
        sub={name}
        footer={
          <>
            <span className="sp" />
            <Button onClick={() => setPw(null)} disabled={pw?.busy}>
              {pw?.unknown ? T('Lukk') : T('Avbryt')}
            </Button>
            {!pw?.unknown && (
              <Button tone="pri" onClick={setPassword} disabled={pw?.busy}>
                {pw?.busy ? T('Lagrer…') : T('Lagre')}
              </Button>
            )}
          </>
        }
      >
        {pw && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!pw.unknown) setPassword();
            }}
          >
            <Row label={T('Nytt passord')}>
              <input
                className="inp"
                type="password"
                autoComplete="new-password"
                aria-label={T('Nytt passord')}
                style={{ width: 240 }}
                value={pw.a}
                disabled={pw.busy || pw.unknown}
                onChange={(e) => setPw({ ...pw, a: e.target.value, msg: null })}
              />
            </Row>
            <Row label={T('Bekreft passord')}>
              <input
                className="inp"
                type="password"
                autoComplete="new-password"
                aria-label={T('Bekreft passord')}
                style={{ width: 240 }}
                value={pw.b}
                disabled={pw.busy || pw.unknown}
                onChange={(e) => setPw({ ...pw, b: e.target.value, msg: null })}
              />
            </Row>
            <p className="hint" style={{ maxWidth: '60ch' }}>
              {T(
                'Det gamle passordet vises ikke og hentes ikke. Den ansatte logger inn med det nye i appen og på nett.',
              )}
            </p>
            {pw.msg && (
              <div className={`al ${pw.unknown ? 'warn' : 'dgr'}`} role="alert">
                <span>{pw.unknown ? '?' : <Icon name="stop" />}</span>
                <span>{pw.msg}</span>
              </div>
            )}
          </form>
        )}
      </Drawer>
      {editing && (
        <EditDrawer
          open
          startTab={editing.tab}
          focus={editing.focus}
          u={u}
          base={base}
          teams={teams}
          seeLoenn={seeLoenn}
          seeSens={seeSens}
          roleText={roleHereText}
          onClose={() => setEditing(false)}
          save={async (body) => {
            try {
              await w.update.mutateAsync({ id: u.id, body: fullBody(u, body) });
              toast(T('Lagret'));
            } catch (e) {
              throw new Error(takenRefusal(e) ?? `${T('Kunne ikke lagre')}: ${(e as Error).message}`);
            }
          }}
        />
      )}
      <TabPanel tab={tab}>
        {tab === '' && <Figures base={base} next={next} week={wk} u={u} abs={abs} />}
        {tab === '' && (
          <Oversikt
            base={base}
            u={u}
            next={next}
            mine={mine}
            abs={abs}
            policy={policy}
            breakMode={modeOf(tenant)}
            roleText={roleHereText}
            jobTitle={jobTitle}
            seeLoenn={seeLoenn}
            seeSens={seeSens}
            onEdit={edit}
            files={
              <Panel
                item
                title={
                  <span className="ovt">
                    <span className="ovi">
                      <Icon name="document" />
                    </span>
                    {T('Dokumenter')}
                  </span>
                }
              >
                <p className="ovnone">
                  {T(
                    'Personlige dokumenter gjelder bare denne ansatte. Felles dokumenter deles av alle i bedriften.',
                  )}
                </p>
                <Link className="ovmore" to={`${base}/dokumenter`}>
                  {T('Åpne dokumenter')} →
                </Link>
              </Panel>
            }
          />
        )}
        {tab === 'vakter' && <VakterTab u={u} />}
        {tab === 'tid' && (
          <TidTab
            u={u}
            mine={mine}
            monday={monday}
            policy={policy}
            breakMode={modeOf(tenant)}
            seeLoenn={seeLoenn}
            onEdit={(f) => (f ? edit(f) : setEditing({ tab: 'arbeid' }))}
          />
        )}
        {tab === 'fravaer' && <FravaerTab u={u} abs={abs} shifts={mine} />}
        {tab === 'tilgjengelighet' && <TilgjengelighetTab userId={u.id} />}
        {tab === 'dokumenter' && <Files userId={u.id} />}
        {tab === 'tilgang' && (
          <TilgangTab
            u={u}
            roleKey={roleHere}
            roleText={roleHereText}
            body={(c) => ({ ...fullBody(u, {}), ...c })}
            onEditTeams={() => setEditing({ tab: 'team' })}
          />
        )}
      </TabPanel>
    </div>
  );
}
